import moment from 'moment';
import '../../bins/parts/bin-path-name';
import '../../bins/parts/bin-profile';
import '../../common/bn-card';
import '../../common/bn-icon';
import '../../common/bn-loading';
import '../../common/bn-property';
import '../../common/bn-modal';
import '../../common/bn-modal-button';
import '../../devices/props/device-serial-number';
import '../../devices/props/device-time-since-last-event';
import '../actions/assign-installer-button';
import '../actions/hold-button';
import '../actions/ready-button';
import '../actions/unassign-button';
import '../actions/work-order-reschedule-button';
import './work-order-view-modal';
import { entityStore } from '~/utility/BinSentry-ui-utility';
import { LitEntity, html, css, renderIf } from '../../utility/lit-entity';
import { sharedStyles } from '../../styles/shared-styles';
import { formatDateShort, formatDateTimeLong, getDateFromString } from '../../utility/dates';

class WorkOrderCard extends LitEntity {
  static get properties() {
    return {
      deviceHref: { type: String, attribute: false },
      latestEventHref: { type: String, attribute: false },
      latestEvent: { type: Object, attribute: false },
      fullPath: { type: Boolean, attribute: 'full-path' },
      hideAddress: { type: Boolean, attribute: 'hide-address' },
      hideState: { type: Boolean, attribute: 'hide-state' },
      historical: { type: Boolean },
    };
  }

  static get styles() {
    return [sharedStyles, css`
      :host {
        display: block;
      }
      .header {
        display: flex;
      }
      .bin-name {
        flex-grow: 1;
        display: inline-block;
        font-weight: bold;
        margin-bottom: .5rem;
      }
      .properties {
        flex-grow: 1;
        display: flex;
        flex-wrap: wrap;
      }
      bn-property {
        margin: .5em 2em 0 0;
        white-space: unset;
      }
      bn-property[label=State] {
        font-weight: bold;
      }
      .actions {
        display: flex;
        align-items: flex-start;
        justify-content: flex-end;
      }
      .actions > * {
        white-space: nowrap;
        margin: 0 0 0 .5rem;
      }
      @media(max-width: 1000px) {
        .header {
          flex-direction: column;
        }
        .properties {
          flex-direction: column;
        }
        bn-property + bn-property {
          margin: .5em 0 0 0;
        }
        .actions {
          flex-wrap: wrap;
        }
        .actions > * {
          margin: 0 0 .5rem .5rem;
        }
      }
      pre {
        margin: 0;
        white-space: pre-wrap;
        font-family: var(--content-font);
      }
      .work-order.hold {
        background-color: var(--gray-20);
        border: 1px dashed var(--gray-50);
        border-radius: 3px;
      }
    `];
  }

  render() {
    if (this.loading || !this.entity) {
      return html`
        <bn-surface>
          <bn-loading></bn-loading>
        </bn-surface>
      `;
    }

    const { state, createdAt, scheduledFor, completedAt, referenceId, location = {}, notes, preDiagnosis, requestType } = this.entity.properties;
    const installerHref = (this.entity.getSubEntityByRel('https://api.binsentry.com/rel/work-order-installer') || {}).href;

    return html`
      <lit-entity .href="${this.deviceHref}" @update="${this._onUpdateDevice}"></lit-entity>
      <lit-entity .href="${this.latestEventHref}" @update="${this._onUpdateLatestEvent}"></lit-entity>
      <bn-surface class="work-order ${state}">
        <div class="body">
          <div class="header">
            ${this._renderBinName()}
            <div class="actions">
              ${this._renderActions()}
            </div>
          </div>
          <div class="properties">
            ${renderIf(!this.hideAddress, () => html`
              <bn-property stacked label="Address">${location.address}</bn-property>
            `)}
            <bn-property stacked label="Reference ID">
              <a href="/work-order-admin/show?workOrderId=${encodeURIComponent(this.href)}">
                <code>${referenceId}</code>
              </a>
            </bn-property>
            <bn-property stacked label="Type">${requestType}</bn-property>
            ${renderIf(!this.hideState, () => html`
              <bn-property stacked label="State">${state}</bn-property>
            `)}
            <bn-property
              stacked
              label="Created"
              title="${formatDateTimeLong(createdAt)}"
            >
              ${this.historical ? this._formatDate(createdAt) : this._formatDateTime(createdAt)}
            </bn-property>
            <bn-property stacked label="Last Read">
              <device-time-since-last-event .href="${this.deviceHref}"></device-time-since-last-event>
            </bn-property>
            ${renderIf(scheduledFor, () => html`
              <bn-property stacked label="Scheduled For" title="${formatDateTimeLong(getDateFromString(scheduledFor))}">${this._formatDate(getDateFromString(scheduledFor))}</bn-property>
            `)}
            ${renderIf(completedAt, () => html`
              <bn-property stacked label="Completed At" title="${formatDateTimeLong(completedAt)}">${this._formatDate(completedAt)}</bn-property>
            `)}
            ${renderIf(installerHref, () => html`
              <bn-property stacked label="Installer">
                <organization-name .href="${installerHref}"></organization-name>
              </bn-property>
            `)}
            ${renderIf(this.deviceHref, () => html`
              <bn-property stacked label="Serial Number">
                <a href="/devices/show?deviceId=${encodeURIComponent(this.deviceHref)}">
                  <device-serial-number .href="${this.deviceHref}"></device-serial-number>
                </a>
              </bn-property>
            `)}
            ${renderIf(this.latestEvent, () => html`
              <bn-property stacked label="Latest Reading">
                <device-event-payload-type payload-type="${this.latestEvent.properties.payloadType}"></device-event-payload-type>
                ${renderIf(this.latestEvent.properties.sensorCriticalError, () => html`
                  (${this.latestEvent.properties.sensorCriticalError.title})
                `)}
              </bn-property>
            `)}
            ${renderIf(preDiagnosis, () => html`
              <bn-property stacked label="Pre-Diagnosis"><pre>${preDiagnosis}</pre></bn-property>
            `)}
            ${renderIf(notes, () => html`
              <bn-property stacked label="Notes"><pre>${notes}</pre></bn-property>
            `)}
          </div>
        </div>
      </bn-surface>
    `;
  }

  _renderBinName() {
    const binHref = this.getHref('https://api.binsentry.com/rel/bin');
    const stopAt = this.fullPath ? 'binsentry' : 'farm';
    return html`
      <div class="bin-name">
        <bin-path-name .href="${binHref}" stop-at-type="${stopAt}"></bin-path-name>
      </div>
    `;
  }

  _renderActions() {
    const actions = [];

    if (this.entity.hasAction('select-installer')) {
      actions.push(html`<assign-installer-button .href="${this.href}" @response="${this._onResponse}"></assign-installer-button>`);
    }

    if (this.entity.hasAction('unassign-work-order')) {
      actions.push(html`<unassign-button .href="${this.href}" @response="${this._onResponse}"></unassign-button>`);
    }

    if (this.entity.hasAction('update-scheduled-for')) {
      actions.push(html`<work-order-reschedule-button .href="${this.href}" @response="${this._onResponse}"></work-order-reschedule-button>`);
    }

    if (this.entity.hasAction('hold-work-order')) {
      actions.push(html`<hold-button .href="${this.href}" @response="${this._onResponse}"></hold-button>`);
    }

    if (this.entity.hasAction('ready-work-order')) {
      actions.push(html`<ready-button .href="${this.href}" @response="${this._onResponse}"></ready-button>`);
    }

    if (this.entity.hasActionByName('reopen-work-order')) {
      actions.push(html`
        <bn-action
          .href="${this.href}"
          actionname="reopen-work-order"
          submittext="Reopen"
          suppressheader
          @response="${this._onResponse}"
        ></bn-action>
      `);
    }

    actions.push(html`
      <bn-modal-button button-class="button--tertiary">
        <span slot="text">View</span>
        <work-order-view-modal .href="${this.href}" slot="modal"></work-order-view-modal>
      </bn-modal-button>
    `);

    return actions;
  }

  _onUpdateDevice(e) {
    const { entity: deviceEntity } = e.detail;
    this.latestEventHref = deviceEntity?.getLink('https://api.binsentry.com/rel/event-latest')?.href;
  }

  _onUpdateLatestEvent(e) {
    this.latestEvent = e.detail?.entity;
  }

  _onResponse() {
    entityStore.invalidateHref(this.href);
    this.dispatchEvent(new CustomEvent('response', { bubbles: true, composed: true }));
  }

  _formatDate(date) {
    if (this.historical) {
      return formatDateShort(date);
    }

    return moment(date).format('MMM D');
  }

  _formatDateTime(date) {
    return moment(date).format('MMM D, h:mm a');
  }

  _entityChanged(entity) {
    super._entityChanged(entity);
    this.deviceHref = (this.entity?.getSubEntityByClasses(['device', 'active']) || {}).href;
  }
}

customElements.define('work-order-list-item', WorkOrderCard);
